import { slotById } from '../config/map';
import {
  compatibilitySlotIdForAnchor,
  queryBuildableSubcell,
} from '../spatial/buildableArea';
import {
  centerAnchorForSlotId,
  legacySlotCenterAnchor,
} from '../spatial/placementMigration';
import {
  legacyCellOfSubcell,
  occupiedSetsOverlap,
  occupiedSubcells,
  productionFootprint,
  type RotationQuarter,
} from '../spatial/footprints';
import type { SubgridCoord } from '../spatial/subgrid';
import type { EntityKind, ParkState, PlacedEntity } from '../types';

export function legacyCellOfAnchor(coord: SubgridCoord): { col: number; row: number } {
  return legacyCellOfSubcell(coord);
}

export function entityAnchor(entity: Pick<PlacedEntity, 'slotId' | 'anchor'>): SubgridCoord {
  if (entity.anchor) {
    return entity.anchor;
  }
  return centerAnchorForSlotId(entity.slotId);
}

export function entityRotation(entity: Pick<PlacedEntity, 'rotation'>): RotationQuarter {
  return entity.rotation ?? 0;
}

export function entityOccupiedSubcells(entity: Pick<PlacedEntity, 'kind' | 'slotId' | 'anchor' | 'rotation'>): SubgridCoord[] {
  return occupiedSubcells(entityAnchor(entity), productionFootprint(entity.kind), entityRotation(entity));
}

export function placementsOverlap(
  a: Pick<PlacedEntity, 'kind' | 'slotId' | 'anchor' | 'rotation'>,
  b: Pick<PlacedEntity, 'kind' | 'slotId' | 'anchor' | 'rotation'>,
): boolean {
  return occupiedSetsOverlap(entityOccupiedSubcells(a), entityOccupiedSubcells(b));
}

export function lockedLandBlocksOccupancy(state: ParkState, occupied: readonly SubgridCoord[]): boolean {
  for (const coord of occupied) {
    const query = queryBuildableSubcell(coord, state.unlockedLandIds);
    if (!query.buildable) {
      return true;
    }
  }
  return false;
}

export function evaluateOccupancy(
  state: ParkState,
  kind: EntityKind,
  anchor: SubgridCoord,
  rotation: RotationQuarter,
  ignoreEntityId?: string,
): { ok: true } | { ok: false; reason: 'occupied' | 'locked' } {
  const candidate: Pick<PlacedEntity, 'kind' | 'slotId' | 'anchor' | 'rotation'> = {
    kind,
    slotId: slotByIdForAnchor(anchor),
    anchor,
    rotation,
  };
  const occupied = entityOccupiedSubcells(candidate);
  if (lockedLandBlocksOccupancy(state, occupied)) {
    return { ok: false, reason: 'locked' };
  }
  for (const other of state.entities) {
    if (other.id === ignoreEntityId) {
      continue;
    }
    if (placementsOverlap(candidate, other)) {
      return { ok: false, reason: 'occupied' };
    }
  }
  return { ok: true };
}

export function occupiedSubcellsForKind(
  kind: EntityKind,
  anchor: SubgridCoord,
  rotation: RotationQuarter,
): SubgridCoord[] {
  return occupiedSubcells(anchor, productionFootprint(kind), rotation);
}

export function slotByIdForAnchor(anchor: SubgridCoord): string {
  return compatibilitySlotIdForAnchor(anchor);
}

export function migrateLegacyPlacedEntity(entity: {
  id: string;
  kind: EntityKind;
  slotId: string;
  level: number;
  storedCoins: number;
  appearance?: PlacedEntity['appearance'];
}): PlacedEntity {
  const slot = slotById(entity.slotId);
  if (!slot) {
    throw new Error(`Unknown legacy slot ${entity.slotId}`);
  }
  return {
    ...entity,
    anchor: legacySlotCenterAnchor(slot),
    rotation: 0,
  };
}

export { centerAnchorForSlotId, productionFootprint };
